import { type FieldErrors, get } from 'react-hook-form';

import type { SecretStatus } from '@/components/atoms/SecretInput';
import type { ProviderRow, SecretName } from '@/domain/providerConfig';
import type { SecretField } from '@/routes/shared/ProviderConfig/schemaFields';

import type { FieldConflict } from '../form/conflictMerge';
import type { SecretOutlook } from '../form/effectiveRows';
import type { ProviderFormValues } from '../form/formModel';

export const SIGNAL_PREFIX = 'signal_mappings.';

/** A secret as one pen's box shows it. */
export interface PenSecretField extends SecretField {
    /** What the box should say while left as it is (penSecretOutlook). */
    outlook?: SecretOutlook;
}

export function conflictAt(
    conflicts: readonly FieldConflict[],
    path: string
): FieldConflict | null {
    return conflicts.find((conflict) => conflict.path === path) ?? null;
}

/** Whether a collapsed part must open: an error or a conflict sits at one of its paths. */
export function needsAttention(
    errors: FieldErrors<ProviderFormValues>,
    conflicts: readonly FieldConflict[],
    paths: readonly string[]
): boolean {
    return paths.some(
        (path) => get(errors, path) !== undefined || conflictAt(conflicts, path) !== null
    );
}

/** The outlook, or the pen's stored flag where no save prediction was given. */
export function knownOutlook(
    row: ProviderRow | null,
    name: SecretName,
    outlook: SecretOutlook | undefined
): SecretOutlook {
    return outlook ?? (row?.credentials[name] === true ? 'stored' : 'none');
}

/** How a pen's box shows a secret's outlook: the status, and whether a site Clear takes it. */
export function penSecretState(
    row: ProviderRow | null,
    name: SecretName,
    outlook: SecretOutlook | undefined
): { status: SecretStatus; pendingClear: boolean } {
    const known = knownOutlook(row, name, outlook);
    if (known === 'inherited') return { status: 'inherited', pendingClear: false };
    if (known === 'none') return { status: 'notSet', pendingClear: false };
    return { status: 'set', pendingClear: known === 'clearedWithSite' };
}
